<script setup lang="ts">
/**
 * The short red marker shown beside a field's label on the canvas, e.g.
 * "Duplicate label". One element can break several rules, so every finding for
 * it is rendered.
 *
 * Deliberately terse: the full sentence lives in the error popover, this is
 * only enough to spot the field while scanning the form.
 */
import { computed } from "vue";
import { AlertTriangleIcon } from "@gohighlevel/ghl-icons/24/outline";
import { useBuilderValidation } from "@/composition/useBuilderValidation";
import { errorInline } from "@/validation/messages";

const props = defineProps<{
  elementIndex: number;
  slideIndex?: number;
}>();

const { unresolvedErrors } = useBuilderValidation();

const findings = computed(() =>
  unresolvedErrors.value.filter(
    (result) =>
      result.target?.elementIndex === props.elementIndex &&
      result.target?.slideIndex === props.slideIndex
  )
);

/** One marker per rule; the same rule never renders twice for one element. */
const markers = computed(() => {
  const seen = new Set<string>();
  return findings.value.filter((result) => {
    if (seen.has(result.ruleId)) return false;
    seen.add(result.ruleId);
    return true;
  });
});
</script>

<template>
  <span
    v-for="marker in markers"
    :key="marker.key"
    class="builder-validation-marker"
    data-testid="builder-validation-marker"
  >
    <AlertTriangleIcon class="h-3.5 w-3.5 shrink-0" />
    <span>{{ errorInline(marker.ruleId) }}</span>
  </span>
</template>

<style scoped>
.builder-validation-marker {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 6px;
  color: #d92d20;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
}
</style>
